// src/components/PublicHeader.js
import React, { useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  ListItemButton,
  Menu,
  MenuItem,
  Collapse,
  Typography
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

// Plan 096:登入流程整包留在 App 網域 agent.rebaz.ai(token→/dashboard 同 origin,
// 不跨域;OAuth redirect URI 也不用改)。公開站搬到 apex rebaz.ai 後,登入鈕用絕對連結
// 跨網域導去 App;在 agent.rebaz.ai 上則只是同網域整頁載入,行為不變。
const APP_LOGIN_URL = 'https://agent.rebaz.ai/login';

// Plan 105:跨產品連結一律用絕對網址(header 為 agent + apex 兩網域共用,
// dispatch/roblox 只活在 rebaz.ai 的 nginx 後面,相對路徑在 agent.rebaz.ai 會 404)。
const REBAZ_ORIGIN = 'https://rebaz.ai';

const PublicHeader = () => {
  const { t, i18n } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [productAnchor, setProductAnchor] = useState(null);   // 桌機「產品」下拉 anchor
  const [productExpand, setProductExpand] = useState(false);  // 手機 drawer「產品」展開
  const navTimeoutRef = useRef(null);

  // 「產品」下拉只在中文介面顯示 — dispatch/roblox 是中文-only 落地頁,
  // 英/日訪客不顯示,避免導到整頁中文的牆(見 Plan 105 決策)。
  const isZh = (i18n.language || 'zh-TW').startsWith('zh');

  // 產品下拉內容:企業方案(客服 / 維修雲)+ 兒童教育(Roblox)。
  // 客服 = 本站首頁(內部路由);dispatch/roblox = 絕對網址整頁載入。
  const productGroups = [
    {
      group: t('header.groupEnterprise'),
      items: [
        { text: t('header.productAiops'), desc: t('header.productAiopsDesc'), href: `${REBAZ_ORIGIN}/aiops/` },
        { text: t('header.productOmni'), desc: t('header.productOmniDesc'), href: '/', internal: true },
        { text: t('header.productDispatch'), desc: t('header.productDispatchDesc'), href: `${REBAZ_ORIGIN}/dispatch/` }
      ]
    },
    {
      group: t('header.groupEducation'),
      items: [
        { text: t('header.productRoblox'), desc: t('header.productRobloxDesc'), href: `${REBAZ_ORIGIN}/roblox/` }
      ]
    }
  ];

  // Navigation items configuration - now using translations
  const navItems = [
    { text: t('header.voiceAgent'), path: '/voice-agent', external: false },
    { text: t('header.features'), path: '/features', external: false },
    { text: t('header.faq'), path: '/faq-public', external: false },
    { text: t('header.column'), path: '/columns/', reload: true },
    { text: t('header.pricing'), path: '/pricing', external: false }
  ];

  // Button styling - Added textTransform: 'none' to prevent uppercase
  const navButtonStyle = {
    fontSize: '16px',
    textTransform: 'none'  // This prevents MUI's default uppercase transformation
  };

  // 產品項目導航(桌機):內部用 React Router,跨產品(dispatch/roblox)整頁載入絕對網址
  const handleProductNav = (item) => {
    setProductAnchor(null);
    if (item.internal) {
      navigate(item.href);
    } else {
      window.location.href = item.href;
    }
  };

  // Handle drawer navigation with improved approach
  const handleDrawerNavigation = (path, isExternal = false, isReload = false) => {
    // Close drawer immediately
    setDrawerOpen(false);

    // Clear any existing navigation timeout
    if (navTimeoutRef.current) {
      clearTimeout(navTimeoutRef.current);
    }

    // Set a new timeout for navigation after drawer closes
    navTimeoutRef.current = setTimeout(() => {
      if (isReload) {
        // 同源靜態頁(/columns 由 nginx 直送 SSG,非 React route)或跨產品絕對網址 → 整頁載入
        window.location.href = path;
      } else if (isExternal) {
        window.open(path, '_blank', 'noopener,noreferrer');
      } else {
        navigate(path, {
          replace: path === location.pathname,
          state: {
            // Add a timestamp to force refresh even on same route
            refresh: Date.now()
          }
        });
      }

      // Clear the timeout reference
      navTimeoutRef.current = null;
    }, 300); // Match the drawer close animation time
  };

  // Handle login button click — 跨網域導去 App 登入頁(見 APP_LOGIN_URL 註解)
  const handleLogin = () => {
    if (isMobile) {
      setDrawerOpen(false);
      setTimeout(() => {
        window.location.href = APP_LOGIN_URL;
      }, 300);
    } else {
      window.location.href = APP_LOGIN_URL;
    }
  };

  // Simple drawer open/close functions
  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: '#3664D1',
        color: 'white',
        boxShadow: 0,
        zIndex: 1200 // Explicit z-index value that's lower than drawer
      }}>
      <Container>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Logo with link to home */}
          <Box
            component={Link}
            to="/"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', textDecoration: 'none' }}
          >
            <img
              src="https://agent-rebaz-ai.s3.ap-northeast-1.amazonaws.com/rebaz_logo_3.png"
              alt="Rebaz Logo"
              style={{ height: '35px', marginRight: '8px', filter: 'brightness(0) invert(1)' }}
            />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* 產品下拉 — 只在中文介面顯示(見 isZh 註解) */}
              {isZh && (
                <>
                  <Button
                    color="inherit"
                    onClick={(e) => setProductAnchor(e.currentTarget)}
                    endIcon={<KeyboardArrowDownIcon />}
                    sx={navButtonStyle}
                  >
                    {t('header.products')}
                  </Button>
                  <Menu
                    anchorEl={productAnchor}
                    open={Boolean(productAnchor)}
                    onClose={() => setProductAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    MenuListProps={{ autoFocusItem: false, sx: { width: 300, py: 1 } }}
                  >
                    {productGroups.map((g, gi) => ([
                      <Typography
                        key={`grp-${gi}`}
                        sx={{ px: 2, pt: gi === 0 ? 0.5 : 1.5, pb: 0.5, fontSize: 11, fontWeight: 700, color: 'text.secondary' }}
                      >
                        {g.group}
                      </Typography>,
                      ...g.items.map((it) => (
                        <MenuItem
                          key={it.text}
                          onClick={() => handleProductNav(it)}
                          sx={{ alignItems: 'flex-start', whiteSpace: 'normal', py: 1, px: 2 }}
                        >
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 15, color: 'text.primary', lineHeight: 1.3 }}>
                              {it.text}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
                              {it.desc}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    ]))}
                  </Menu>
                </>
              )}
              {navItems.map((item) => (
                item.reload ? (
                  // 同源靜態頁(/columns 由 nginx 直送 SSG)→ 整頁載入,非 React Link
                  <Button
                    key={item.text}
                    color="inherit"
                    component="a"
                    href={item.path}
                    sx={navButtonStyle}
                  >
                    {item.text}
                  </Button>
                ) : item.external ? (
                  // External link
                  <Button
                    key={item.text}
                    color="inherit"
                    component="a"
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={navButtonStyle}
                  >
                    {item.text}
                  </Button>
                ) : (
                  // Internal link
                  <Button
                    key={item.text}
                    color="inherit"
                    component={Link}
                    to={item.path}
                    sx={navButtonStyle}
                  >
                    {item.text}
                  </Button>
                )
              ))}

              {/* Language Selector for Desktop */}
              <Box sx={{ mx: 2 }}>
                <LanguageSelector variant="button" />
              </Box>

              {/* Login/Register button */}
              <Button
                onClick={handleLogin}
                variant="contained"
                color="inherit"
                sx={{
                  fontSize: '16px',
                  textTransform: 'none',  // Also apply to login button
                  backgroundColor: '#000000',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#222222'
                  }
                }}
              >
                {t('header.login')}
              </Button>
            </Box>
          ) : (
            // Mobile - Language Selector Icon and Menu Icon
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LanguageSelector variant="icon" />
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={openDrawer}
                sx={{ ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* Mobile Navigation Drawer */}
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={closeDrawer}
            SlideProps={{ timeout: 300 }}
            ModalProps={{
              keepMounted: true
            }}
          >
            <Box sx={{ width: 250 }} role="presentation">
              <List>
                {/* 產品(可展開)— 只在中文介面顯示 */}
                {isZh && (
                  <>
                    <ListItemButton onClick={() => setProductExpand(!productExpand)}>
                      <ListItemText primary={t('header.products')} />
                      {productExpand ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={productExpand} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {productGroups.flatMap((g) => g.items).map((it) => (
                          <ListItemButton
                            key={it.text}
                            sx={{ pl: 4 }}
                            onClick={() => handleDrawerNavigation(it.href, false, !it.internal)}
                          >
                            <ListItemText primary={it.text} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                    <Divider />
                  </>
                )}
                {navItems.map((item) => (
                  <ListItemButton
                    key={`${item.text}-${location.pathname === item.path ? 'active' : 'inactive'}`}
                    onClick={() => handleDrawerNavigation(item.path, item.external, item.reload)}
                    selected={!item.external && location.pathname === item.path}
                  >
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                ))}
              </List>
              <Divider />
              <List>
                <ListItemButton onClick={() => { setDrawerOpen(false); window.location.href = APP_LOGIN_URL; }}>
                  <ListItemText primary={t('header.login')} />
                </ListItemButton>
              </List>
            </Box>
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default PublicHeader;
